import { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify'; // Import toast for notifications
import { AddOperationDetails, UpdateOperationDetails } from '../AttendanceApis';

const AddEditOperationModal = ({
  setOperationTableData,
  showAddEditModal,
  setShowAddEditModal,
  editOperationTableRow,
  setEditOperationTableRow,
  isOperationEditMode,
  setIsOperationEditMode,
  canWriteValue
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const initialValues = {
    operationName: isOperationEditMode ? editOperationTableRow?.operationName : "",
    isActive: isOperationEditMode ? editOperationTableRow?.isActive.toString() : "true",
  };

  const validationSchema = Yup.object().shape({
    operationName: Yup.string()
      .matches(/^[^\s]/, "Operation cannot start with a space.")
      .matches(/^[a-zA-Z0-9 ]*$/, "Operation cannot contain special characters.")
      .min(3, "Operation minimum contain 3 letters.")
      .required("Operation is required."),
    isActive: Yup.boolean(),
  });

  // Function to handle Add Operation
  const handleAddOperation = async (values, resetForm) => {
    setIsLoading(true);
    const addedOperation = await AddOperationDetails(values);
    if (addedOperation.status === 200) {
      toast.success(addedOperation.data.message);
      setOperationTableData();  // Refresh the table
      setShowAddEditModal(false);  // Close the modal
      setEditOperationTableRow(null);  // Clear the edit state
      setIsLoading(false);
      resetForm();
    } else {
      toast.error(addedOperation.response.data);
      setIsLoading(false);
    }
  };

  // Function to handle Update Operation
  const handleUpdateOperation = async (values, resetForm) => {
    const updatedValues = {...values, operationId: editOperationTableRow.operationId}
    setIsLoading(true);
    const updatedOperationResponse = await UpdateOperationDetails(updatedValues);
    if (updatedOperationResponse.status === 200) {
      toast.success(updatedOperationResponse.data);
      setOperationTableData();  // Refresh the table
      setShowAddEditModal(false);  // Close the modal
      setEditOperationTableRow(null);  // Clear the edit state
      setIsLoading(false);
      setIsOperationEditMode(false);
      resetForm();
    } else {
      toast.error(updatedOperationResponse.response.data);
      setIsLoading(false);
    }
  };

  // Handle form submission (add or update operation)
  const onSubmit = async (values, { resetForm }) => {
    const updatedValues = {...values, isActive: values.isActive === 'true'}
    if (editOperationTableRow) {
      await handleUpdateOperation(updatedValues, resetForm);
    } else {
      await handleAddOperation(updatedValues, resetForm);
    }
  };

  useEffect(() => {
    if (!editOperationTableRow) {
      setEditOperationTableRow(null);
    }
  }, [editOperationTableRow]);

  const handleClose = () => {
    setShowAddEditModal(false);
    setEditOperationTableRow(null);
  };

  return (
    <Modal
      show={showAddEditModal}
      onHide={handleClose}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          {editOperationTableRow ? "Edit Operation" : "Add Operation"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ errors, touched, resetForm, values }) => (
            <FormikForm>
              <div className="row">
                <div className="col-md-8 mb-3">
                  <label htmlFor="operationName">
                    Operation <span className="text-danger">*</span>
                  </label>
                  <Field
                    type="text"
                    name="operationName"
                    placeholder="Enter Operation Name"
                    maxLength={50}
                    className={`form-control ${errors.operationName && touched.operationName ? "" : ""}`}
                  />
                  <ErrorMessage name="operationName" component="div" style={{ color: 'red' }} className='small' />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label">
                    Status<span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <div className="form-group" style={{ display: "flex", alignItems: 'center' }}>
                    <div className="form-check d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="activeRadio"
                        value="true"
                      />
                      <label className="form-check-label" htmlFor="activeRadio">
                        Active
                      </label>
                    </div>
                    <div className="form-check ms-4 d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="inactiveRadio"
                        value="false"
                      />
                      <label className="form-check-label" htmlFor="inactiveRadio">
                        InActive
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <Modal.Footer className="d-flex justify-content-center">
                {canWriteValue && <button type="submit" className="deployment-button">
                  {editOperationTableRow ?
                    isLoading ?
                      <l-bouncy
                        size="25"
                        speed="1.75"
                        color="white"
                      ></l-bouncy> : <> <i className="fa-solid fa-check done-container"></i> Update</> :
                    isLoading ? <l-bouncy
                      size="25"
                      speed="1.75"
                      color="white"
                    ></l-bouncy> : <> <i className="fa-solid fa-check done-container"></i> Submit </>}
                </button>}
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditOperationModal;

